// The languages of an event's RSVP form (round 4, wireframe "Suite Event
// RSVP": English, French, Serbian, Albanian, Macedonian). The guest pages
// (the personal invitation and the shared link) show their fixed wording in
// the form's language; staff write their own questions per language
// (QuestionBody.translations). Serbian is in Latin script, Macedonian in
// Cyrillic. The answers keep the English option values, so the results
// count the same whatever the language.

export const RSVP_LANGUAGES = ["en", "fr", "sr", "sq", "mk"] as const;
export type RsvpLanguage = (typeof RSVP_LANGUAGES)[number];
export const RSVP_LANGUAGE_LABELS: Record<RsvpLanguage, string> = { en: "English", fr: "French", sr: "Serbian", sq: "Albanian", mk: "Macedonian" };

export const isRsvpLanguage = (v: string | null | undefined): v is RsvpLanguage => (RSVP_LANGUAGES as readonly string[]).includes(v ?? "");

export type GuestLabels = {
  invited: string;
  register: string;
  when: string;
  where: string;
  organisedBy: string;
  map: string;
  addGoogle: string;
  addOutlook: string;
  downloadIcs: string;
  saveTheDate: string;
  willYouAttend: string;
  yes: string;
  yesBothDays: string;
  yesAllDays: string;
  onlyDay1: string;
  onlyDay2: string;
  no: string;
  maybe: string;
  howJoin: string;
  inPerson: string;
  online: string;
  onlineHint: string;
  onlineEvent: string;
  yourName: string;
  email: string;
  emailHint: string;
  organisation: string;
  organisationHint: string;
  position: string;
  /** Round 5 (AN2): "Type of organisation" and its options (the stored values stay the English keys). */
  organisationType: string;
  organisationTypes: { government: string; civil_society: string; academia: string; private_sector: string; international_org: string; media: string; other: string };
  gender: string;
  genderHint: string;
  choose: string;
  genderWoman: string;
  genderMan: string;
  genderNonBinary: string;
  genderOther: string;
  genderPreferNot: string;
  travel: string;
  travelYesHint: string;
  dietary: string;
  photo: string;
  /** "{org}" is the organisation's name. */
  consentStore: string;
  /** Fix round 1 (C-3): the optional second tick, consent to future contact. */
  keepInformed: string;
  /** The personal invitation's optional "keep me informed" box; "{org}" is the organisation. */
  consentKeep: string;
  privacyNotice: string;
  send: string;
  sending: string;
  closedTitle: string;
  fullTitle: string;
  answerAttendance: string;
  answerTravel: string;
  answerJoining: string;
  answerQuestion: string;
  enterName: string;
  enterEmail: string;
  consentMissing: string;
  /** Round 5 (EM9): photo consent as a Yes/No question everyone coming must answer (never pre-ticked). */
  photoQuestion: string;
  answerPhoto: string;
  /** Round 5 (EM7): the answer as it stands, above the form, and the button that opens the form again. */
  updateAnswer: string;
  answerRegistered: string;
  answerWaitlisted: string;
  answerDeclined: string;
  answerMaybe: string;
  thanksTitle: string;
  /** "{email}" is the address. */
  thanksBody: string;
  /** Round 5: the invitation text at the top of the RSVP form when staff did not write their own ({first name}, {salutation}, {event title}). */
  introDefault: string;
  /** Round 5: what {first name} becomes when the form does not know the person ("Dear guest,"); empty = left out ("Bonjour,"). */
  guestName: string;
  /** E1 (retest 30 Oct): the programme for guests. */
  aboutEvent: string;
  programme: string;
  practical: string;
  addToCalendar: string;
  /** "{n}" is the day's number. */
  dayN: string;
  speaker: string;
  moderator: string;
  parallel: string;
  reply: string;
  /** E19 (retest 30 Oct): the RSVP in two steps. */
  next: string;
  yourDetails: string;
  yourAnswer: string;
  change: string;
  /** EV-F6 (round 3 feedback): title, first and last name, country, phone, accessibility needs, the required marker. */
  personTitle: string;
  firstName: string;
  lastName: string;
  country: string;
  phone: string;
  accessNeeds: string;
  required: string;
  /** EV-F5: above answers filled from what the person told us before. */
  stillCorrect: string;
  /** The record has needs on file (the values are never shown on the guest page). */
  needsOnFile: string;
};

export const GUEST_LABELS: Record<RsvpLanguage, GuestLabels> = {
  en: {
    invited: "You are invited: {title}",
    register: "Register: {title}",
    when: "When",
    where: "Where",
    organisedBy: "Organised by",
    map: "Map",
    addGoogle: "Add to Google Calendar",
    addOutlook: "Add to Outlook",
    downloadIcs: "Download .ics (Apple and others)",
    saveTheDate: "Save the date now. You will get a confirmed calendar invite once you reply.",
    willYouAttend: "Will you attend?",
    yes: "Yes",
    yesBothDays: "Yes, both days",
    yesAllDays: "Yes, all days",
    onlyDay1: "Only day 1",
    onlyDay2: "Only day 2",
    no: "No",
    maybe: "Maybe",
    howJoin: "How will you join?",
    inPerson: "In person",
    online: "Online",
    onlineHint: "The link comes with your confirmation and calendar invite.",
    onlineEvent: "This event is online. The link comes with your confirmation and calendar invite.",
    yourName: "Your name",
    email: "Email",
    emailHint: "We send your confirmation here.",
    organisation: "Organisation",
    organisationHint: "From our records. Correct it if it changed.",
    position: "Position",
    organisationType: "Type of organisation",
    organisationTypes: { government: "Government", civil_society: "Civil society", academia: "Academia and research", private_sector: "Private sector", international_org: "International organisation", media: "Media", other: "Other" },
    gender: "Gender",
    genderHint: "Optional. Used for balance figures.",
    choose: "Choose",
    genderWoman: "Woman",
    genderMan: "Man",
    genderNonBinary: "Non-binary",
    genderOther: "I describe myself another way",
    genderPreferNot: "I prefer not to say",
    travel: "Do you need travel support?",
    travelYesHint: "We will contact you about travel",
    dietary: "Dietary requirements",
    photo: "I agree to photos being taken for project reporting",
    consentStore: "Store my details to organise this event",
    keepInformed: "Keep me informed about the project",
    consentKeep: "{org} may keep my contact details to invite me to project activities",
    privacyNotice: "Privacy notice",
    send: "Send my answer",
    sending: "Sending…",
    closedTitle: "This form is closed.",
    fullTitle: "The event is full",
    answerAttendance: "Tell us if you will attend.",
    answerTravel: "Tell us if you need travel support.",
    answerJoining: "Tell us how you will join.",
    answerQuestion: "Answer this question.",
    enterName: "Enter your name.",
    enterEmail: "Enter your email address, like name@example.org.",
    consentMissing: "Tick the box to agree that we store your details. We need it to register you.",
    photoQuestion: "Do you agree to photos being taken of you for project reporting?",
    answerPhoto: "Tell us if you agree to photos.",
    updateAnswer: "Update my answer",
    answerRegistered: "Your answer: yes. You are registered.",
    answerWaitlisted: "Your answer: yes. You are on the waiting list.",
    answerDeclined: "Your answer: no. You are not attending.",
    answerMaybe: "Your answer: maybe.",
    thanksTitle: "Check your email to confirm your reply",
    thanksBody: "We have sent an email to {email}. Your reply counts only once you click the link in it. The link works for 48 hours. Did not get it? Check your spam folder.",
    introDefault: "Dear {first name},\n\nWe would like to invite you to our event. You find the details below; please let us know whether you can come.",
    guestName: "guest",
    aboutEvent: "About the event",
    programme: "Programme",
    practical: "Practical details",
    addToCalendar: "Add to calendar",
    dayN: "Day {n}",
    speaker: "Speaker",
    moderator: "Moderator",
    parallel: "Parallel session",
    reply: "Reply to the invitation",
    next: "Continue",
    yourDetails: "Your details",
    yourAnswer: "Your answer",
    change: "Change",
    personTitle: "Title (for example Dr, Ms, Mr)",
    firstName: "First name",
    lastName: "Last name",
    country: "Country",
    phone: "Phone",
    accessNeeds: "Accessibility needs",
    required: "required",
    stillCorrect: "Is this still correct? We filled in what you told us before. Change anything that is out of date.",
    needsOnFile: "We have dietary or accessibility needs on file for you from an earlier event. Fill them in here only if they changed.",
  },
  fr: {
    invited: "Vous êtes invité(e) : {title}",
    register: "Inscription : {title}",
    when: "Quand",
    where: "Où",
    organisedBy: "Organisé par",
    map: "Plan",
    addGoogle: "Ajouter à Google Agenda",
    addOutlook: "Ajouter à Outlook",
    downloadIcs: "Télécharger le fichier .ics (Apple et autres)",
    saveTheDate: "Réservez la date dès maintenant. Vous recevrez une invitation confirmée dans votre agenda après votre réponse.",
    willYouAttend: "Serez-vous présent(e) ?",
    yes: "Oui",
    yesBothDays: "Oui, les deux jours",
    yesAllDays: "Oui, tous les jours",
    onlyDay1: "Seulement le jour 1",
    onlyDay2: "Seulement le jour 2",
    no: "Non",
    maybe: "Peut-être",
    howJoin: "Comment participerez-vous ?",
    inPerson: "En personne",
    online: "En ligne",
    onlineHint: "Le lien est joint à votre confirmation et à l'invitation d'agenda.",
    onlineEvent: "Cet événement a lieu en ligne. Le lien est joint à votre confirmation et à l'invitation d'agenda.",
    yourName: "Votre nom",
    email: "Adresse e-mail",
    emailHint: "Nous y envoyons votre confirmation.",
    organisation: "Organisation",
    organisationHint: "Selon nos informations. Corrigez-la si elle a changé.",
    position: "Fonction",
    organisationType: "Type d'organisation",
    organisationTypes: { government: "Administration publique", civil_society: "Société civile", academia: "Université et recherche", private_sector: "Secteur privé", international_org: "Organisation internationale", media: "Médias", other: "Autre" },
    gender: "Genre",
    genderHint: "Facultatif. Utilisé pour les chiffres sur l'équilibre.",
    choose: "Choisir",
    genderWoman: "Femme",
    genderMan: "Homme",
    genderNonBinary: "Non binaire",
    genderOther: "Je me décris autrement",
    genderPreferNot: "Je préfère ne pas répondre",
    travel: "Avez-vous besoin d'une prise en charge du voyage ?",
    travelYesHint: "Nous vous contacterons au sujet du voyage",
    dietary: "Régime alimentaire",
    photo: "J'accepte que des photos soient prises pour les rapports du projet",
    consentStore: "Conserver mes données pour organiser cet événement",
    keepInformed: "Me tenir informé(e) du projet",
    consentKeep: "{org} peut conserver mes coordonnées pour m'inviter aux activités du projet",
    privacyNotice: "Avis de confidentialité",
    send: "Envoyer ma réponse",
    sending: "Envoi…",
    closedTitle: "Ce formulaire est fermé.",
    fullTitle: "L'événement est complet",
    answerAttendance: "Indiquez si vous serez présent(e).",
    answerTravel: "Indiquez si vous avez besoin d'une prise en charge du voyage.",
    answerJoining: "Indiquez comment vous participerez.",
    answerQuestion: "Répondez à cette question.",
    enterName: "Saisissez votre nom.",
    enterEmail: "Saisissez votre adresse e-mail, par exemple nom@exemple.org.",
    consentMissing: "Cochez la case pour accepter que nous conservions vos données. Nous en avons besoin pour vous inscrire.",
    photoQuestion: "Acceptez-vous d'être photographié(e) pour les rapports du projet ?",
    answerPhoto: "Indiquez si vous acceptez les photos.",
    updateAnswer: "Modifier ma réponse",
    answerRegistered: "Votre réponse : oui. Vous êtes inscrit(e).",
    answerWaitlisted: "Votre réponse : oui. Vous êtes sur la liste d'attente.",
    answerDeclined: "Votre réponse : non. Vous ne participez pas.",
    answerMaybe: "Votre réponse : peut-être.",
    thanksTitle: "Vérifiez vos e-mails pour confirmer votre réponse",
    thanksBody: "Nous avons envoyé un e-mail à {email}. Votre réponse ne compte qu'une fois que vous avez cliqué sur le lien qu'il contient. Le lien est valable 48 heures. Rien reçu ? Vérifiez vos courriers indésirables.",
    introDefault: "Bonjour {first name},\n\nNous avons le plaisir de vous inviter à notre événement. Vous trouverez les détails ci-dessous ; merci de nous dire si vous pourrez venir.",
    guestName: "",
    aboutEvent: "À propos de l'événement",
    programme: "Programme",
    practical: "Informations pratiques",
    addToCalendar: "Ajouter au calendrier",
    dayN: "Jour {n}",
    speaker: "Intervenant(e)",
    moderator: "Modérateur/modératrice",
    parallel: "Session parallèle",
    reply: "Répondre à l'invitation",
    next: "Continuer",
    yourDetails: "Vos coordonnées",
    yourAnswer: "Votre réponse",
    change: "Modifier",
    personTitle: "Titre (par exemple Dr, Mme, M.)",
    firstName: "Prénom",
    lastName: "Nom",
    country: "Pays",
    phone: "Téléphone",
    accessNeeds: "Besoins d'accessibilité",
    required: "obligatoire",
    stillCorrect: "Ces informations sont-elles toujours exactes ? Nous avons repris ce que vous nous avez indiqué la dernière fois. Modifiez ce qui n'est plus à jour.",
    needsOnFile: "Nous avons déjà des besoins alimentaires ou d'accessibilité pour vous, d'un événement précédent. Indiquez-les ici seulement s'ils ont changé.",
  },
  sr: {
    invited: "Pozvani ste: {title}",
    register: "Prijava: {title}",
    when: "Kada",
    where: "Gde",
    organisedBy: "Organizator",
    map: "Mapa",
    addGoogle: "Dodaj u Google kalendar",
    addOutlook: "Dodaj u Outlook",
    downloadIcs: "Preuzmi .ics (Apple i drugi)",
    saveTheDate: "Zabeležite datum već sada. Potvrđenu pozivnicu za kalendar dobićete kada odgovorite.",
    willYouAttend: "Da li ćete prisustvovati?",
    yes: "Da",
    yesBothDays: "Da, oba dana",
    yesAllDays: "Da, svih dana",
    onlyDay1: "Samo prvi dan",
    onlyDay2: "Samo drugi dan",
    no: "Ne",
    maybe: "Možda",
    howJoin: "Kako ćete učestvovati?",
    inPerson: "Lično",
    online: "Onlajn",
    onlineHint: "Link stiže uz vašu potvrdu i pozivnicu za kalendar.",
    onlineEvent: "Ovaj događaj je onlajn. Link stiže uz vašu potvrdu i pozivnicu za kalendar.",
    yourName: "Vaše ime",
    email: "Imejl",
    emailHint: "Ovde šaljemo vašu potvrdu.",
    organisation: "Organizacija",
    organisationHint: "Iz naše evidencije. Ispravite ako se promenilo.",
    position: "Funkcija",
    organisationType: "Vrsta organizacije",
    organisationTypes: { government: "Državna uprava", civil_society: "Civilno društvo", academia: "Akademska zajednica i istraživanje", private_sector: "Privatni sektor", international_org: "Međunarodna organizacija", media: "Mediji", other: "Drugo" },
    gender: "Pol",
    genderHint: "Nije obavezno. Koristi se za podatke o ravnoteži.",
    choose: "Izaberite",
    genderWoman: "Žensko",
    genderMan: "Muško",
    genderNonBinary: "Nebinarno",
    genderOther: "Opisujem se drugačije",
    genderPreferNot: "Ne želim da navedem",
    travel: "Da li vam je potrebna podrška za putovanje?",
    travelYesHint: "Javićemo vam se u vezi sa putovanjem",
    dietary: "Posebna ishrana",
    photo: "Slažem se da se fotografije koriste za izveštaje projekta",
    consentStore: "Čuvajte moje podatke radi organizacije ovog događaja",
    keepInformed: "Obaveštavajte me o projektu",
    consentKeep: "{org} može da čuva moje kontakt podatke da bi me pozivao na aktivnosti projekta",
    privacyNotice: "Obaveštenje o privatnosti",
    send: "Pošalji odgovor",
    sending: "Slanje…",
    closedTitle: "Ovaj obrazac je zatvoren.",
    fullTitle: "Sva mesta su popunjena",
    answerAttendance: "Recite nam da li ćete prisustvovati.",
    answerTravel: "Recite nam da li vam je potrebna podrška za putovanje.",
    answerJoining: "Recite nam kako ćete učestvovati.",
    answerQuestion: "Odgovorite na ovo pitanje.",
    enterName: "Unesite svoje ime.",
    enterEmail: "Unesite imejl adresu, na primer ime@primer.org.",
    consentMissing: "Označite polje da biste se složili da čuvamo vaše podatke. Potrebno je za prijavu.",
    photoQuestion: "Da li se slažete da budete fotografisani za izveštaje projekta?",
    answerPhoto: "Recite nam da li se slažete sa fotografisanjem.",
    updateAnswer: "Izmeni moj odgovor",
    answerRegistered: "Vaš odgovor: da. Prijavljeni ste.",
    answerWaitlisted: "Vaš odgovor: da. Na listi čekanja ste.",
    answerDeclined: "Vaš odgovor: ne. Ne dolazite.",
    answerMaybe: "Vaš odgovor: možda.",
    thanksTitle: "Proverite imejl da potvrdite odgovor",
    thanksBody: "Poslali smo imejl na {email}. Vaš odgovor važi tek kada kliknete na link u njemu. Link važi 48 sati. Niste ga dobili? Proverite folder za neželjenu poštu.",
    introDefault: "Poštovani {first name},\n\nSa zadovoljstvom Vas pozivamo na naš događaj. Detalje ćete naći u nastavku; molimo Vas da nam javite da li možete da dođete.",
    guestName: "",
    aboutEvent: "O događaju",
    programme: "Program",
    practical: "Praktične informacije",
    addToCalendar: "Dodaj u kalendar",
    dayN: "Dan {n}",
    speaker: "Govornik/govornica",
    moderator: "Moderator/moderatorka",
    parallel: "Paralelna sesija",
    reply: "Odgovorite na poziv",
    next: "Nastavi",
    yourDetails: "Vaši podaci",
    yourAnswer: "Vaš odgovor",
    change: "Promeni",
    personTitle: "Titula (na primer dr, gđa, g.)",
    firstName: "Ime",
    lastName: "Prezime",
    country: "Država",
    phone: "Telefon",
    accessNeeds: "Potrebe pristupačnosti",
    required: "obavezno",
    stillCorrect: "Da li je ovo još uvek tačno? Popunili smo ono što ste nam ranije rekli. Promenite sve što više nije tačno.",
    needsOnFile: "Već imamo vaše potrebe u vezi sa ishranom ili pristupačnošću sa ranijeg događaja. Upišite ih ovde samo ako su se promenile.",
  },
  sq: {
    invited: "Jeni të ftuar: {title}",
    register: "Regjistrohu: {title}",
    when: "Kur",
    where: "Ku",
    organisedBy: "Organizuar nga",
    map: "Harta",
    addGoogle: "Shto në Google Calendar",
    addOutlook: "Shto në Outlook",
    downloadIcs: "Shkarko .ics (Apple dhe të tjera)",
    saveTheDate: "Shënoni datën që tani. Do të merrni një ftesë të konfirmuar në kalendar pasi të përgjigjeni.",
    willYouAttend: "A do të merrni pjesë?",
    yes: "Po",
    yesBothDays: "Po, të dyja ditët",
    yesAllDays: "Po, të gjitha ditët",
    onlyDay1: "Vetëm dita 1",
    onlyDay2: "Vetëm dita 2",
    no: "Jo",
    maybe: "Ndoshta",
    howJoin: "Si do të merrni pjesë?",
    inPerson: "Personalisht",
    online: "Në internet",
    onlineHint: "Lidhja vjen me konfirmimin dhe ftesën e kalendarit.",
    onlineEvent: "Ky aktivitet zhvillohet në internet. Lidhja vjen me konfirmimin dhe ftesën e kalendarit.",
    yourName: "Emri juaj",
    email: "Email",
    emailHint: "Konfirmimin jua dërgojmë këtu.",
    organisation: "Organizata",
    organisationHint: "Nga të dhënat tona. Korrigjojeni nëse ka ndryshuar.",
    position: "Pozita",
    organisationType: "Lloji i organizatës",
    organisationTypes: { government: "Administrata publike", civil_society: "Shoqëria civile", academia: "Akademia dhe kërkimi", private_sector: "Sektori privat", international_org: "Organizatë ndërkombëtare", media: "Media", other: "Tjetër" },
    gender: "Gjinia",
    genderHint: "Opsionale. Përdoret për shifrat e balancës.",
    choose: "Zgjidhni",
    genderWoman: "Grua",
    genderMan: "Burrë",
    genderNonBinary: "Jobinare",
    genderOther: "E përshkruaj veten ndryshe",
    genderPreferNot: "Preferoj të mos e them",
    travel: "A keni nevojë për mbështetje për udhëtimin?",
    travelYesHint: "Do t'ju kontaktojmë për udhëtimin",
    dietary: "Kërkesa ushqimore",
    photo: "Pajtohem që të bëhen fotografi për raportimin e projektit",
    consentStore: "Ruani të dhënat e mia për të organizuar këtë aktivitet",
    keepInformed: "Më mbani të informuar për projektin",
    consentKeep: "{org} mund të ruajë të dhënat e mia të kontaktit për të më ftuar në aktivitetet e projektit",
    privacyNotice: "Njoftimi i privatësisë",
    send: "Dërgo përgjigjen",
    sending: "Duke dërguar…",
    closedTitle: "Ky formular është i mbyllur.",
    fullTitle: "Aktiviteti është plot",
    answerAttendance: "Na tregoni nëse do të merrni pjesë.",
    answerTravel: "Na tregoni nëse keni nevojë për mbështetje për udhëtimin.",
    answerJoining: "Na tregoni si do të merrni pjesë.",
    answerQuestion: "Përgjigjuni kësaj pyetjeje.",
    enterName: "Shkruani emrin tuaj.",
    enterEmail: "Shkruani adresën e emailit, p.sh. emri@shembull.org.",
    consentMissing: "Shënoni kutinë për të pranuar që të ruajmë të dhënat tuaja. Na duhet për t'ju regjistruar.",
    photoQuestion: "A pajtoheni që të fotografoheni për raportimin e projektit?",
    answerPhoto: "Na tregoni nëse pajtoheni me fotografitë.",
    updateAnswer: "Ndrysho përgjigjen time",
    answerRegistered: "Përgjigjja juaj: po. Jeni të regjistruar.",
    answerWaitlisted: "Përgjigjja juaj: po. Jeni në listën e pritjes.",
    answerDeclined: "Përgjigjja juaj: jo. Nuk do të merrni pjesë.",
    answerMaybe: "Përgjigjja juaj: ndoshta.",
    thanksTitle: "Kontrolloni emailin për të konfirmuar përgjigjen",
    thanksBody: "Kemi dërguar një email në {email}. Përgjigjja juaj vlen vetëm pasi të klikoni lidhjen në të. Lidhja vlen 48 orë. Nuk e morët? Kontrolloni dosjen e postës së padëshiruar.",
    introDefault: "Të nderuar {first name},\n\nKemi kënaqësinë t'ju ftojmë në eventin tonë. Detajet i gjeni më poshtë; ju lutemi na tregoni nëse mund të vini.",
    guestName: "",
    aboutEvent: "Rreth eventit",
    programme: "Programi",
    practical: "Informacione praktike",
    addToCalendar: "Shto në kalendar",
    dayN: "Dita {n}",
    speaker: "Folës/folëse",
    moderator: "Moderator/moderatore",
    parallel: "Sesion paralel",
    reply: "Përgjigjuni ftesës",
    next: "Vazhdo",
    yourDetails: "Të dhënat tuaja",
    yourAnswer: "Përgjigjja juaj",
    change: "Ndrysho",
    personTitle: "Titulli (për shembull Dr., Znj., Z.)",
    firstName: "Emri",
    lastName: "Mbiemri",
    country: "Shteti",
    phone: "Telefoni",
    accessNeeds: "Nevojat për aksesueshmëri",
    required: "e detyrueshme",
    stillCorrect: "A është ende e saktë? Plotësuam atë që na thatë më parë. Ndryshoni çdo gjë që nuk është më e saktë.",
    needsOnFile: "Kemi nevojat tuaja për ushqimin ose aksesueshmërinë nga një event i mëparshëm. Plotësojini këtu vetëm nëse kanë ndryshuar.",
  },
  mk: {
    invited: "Поканети сте: {title}",
    register: "Пријава: {title}",
    when: "Кога",
    where: "Каде",
    organisedBy: "Организатор",
    map: "Мапа",
    addGoogle: "Додај во Google календар",
    addOutlook: "Додај во Outlook",
    downloadIcs: "Преземи .ics (Apple и други)",
    saveTheDate: "Запишете го датумот уште сега. Потврдена покана за календар ќе добиете кога ќе одговорите.",
    willYouAttend: "Дали ќе присуствувате?",
    yes: "Да",
    yesBothDays: "Да, двата дена",
    yesAllDays: "Да, сите денови",
    onlyDay1: "Само првиот ден",
    onlyDay2: "Само вториот ден",
    no: "Не",
    maybe: "Можеби",
    howJoin: "Како ќе учествувате?",
    inPerson: "Лично",
    online: "Онлајн",
    onlineHint: "Линкот стига со вашата потврда и поканата за календар.",
    onlineEvent: "Овој настан е онлајн. Линкот стига со вашата потврда и поканата за календар.",
    yourName: "Вашето име",
    email: "Е-пошта",
    emailHint: "Тука ја испраќаме вашата потврда.",
    organisation: "Организација",
    organisationHint: "Од нашата евиденција. Поправете ако е променето.",
    position: "Позиција",
    organisationType: "Вид на организација",
    organisationTypes: { government: "Државна управа", civil_society: "Граѓанско општество", academia: "Академска заедница и истражување", private_sector: "Приватен сектор", international_org: "Меѓународна организација", media: "Медиуми", other: "Друго" },
    gender: "Пол",
    genderHint: "Незадолжително. Се користи за бројките за рамнотежа.",
    choose: "Изберете",
    genderWoman: "Жена",
    genderMan: "Маж",
    genderNonBinary: "Небинарно",
    genderOther: "Се опишувам поинаку",
    genderPreferNot: "Претпочитам да не кажам",
    travel: "Дали ви е потребна поддршка за патување?",
    travelYesHint: "Ќе ве контактираме во врска со патувањето",
    dietary: "Посебна исхрана",
    photo: "Се согласувам да се фотографира за известувањето на проектот",
    consentStore: "Чувајте ги моите податоци за организирање на овој настан",
    keepInformed: "Информирајте ме за проектот",
    consentKeep: "{org} може да ги чува моите податоци за контакт за да ме кани на активности на проектот",
    privacyNotice: "Известување за приватност",
    send: "Испрати одговор",
    sending: "Се испраќа…",
    closedTitle: "Овој образец е затворен.",
    fullTitle: "Настанот е пополнет",
    answerAttendance: "Кажете ни дали ќе присуствувате.",
    answerTravel: "Кажете ни дали ви е потребна поддршка за патување.",
    answerJoining: "Кажете ни како ќе учествувате.",
    answerQuestion: "Одговорете на ова прашање.",
    enterName: "Внесете го вашето име.",
    enterEmail: "Внесете адреса за е-пошта, на пример ime@primer.org.",
    consentMissing: "Означете го полето за да се согласите да ги чуваме вашите податоци. Потребно е за пријавата.",
    photoQuestion: "Дали се согласувате да бидете фотографирани за известувањето на проектот?",
    answerPhoto: "Кажете ни дали се согласувате со фотографирање.",
    updateAnswer: "Промени го мојот одговор",
    answerRegistered: "Вашиот одговор: да. Пријавени сте.",
    answerWaitlisted: "Вашиот одговор: да. На листата на чекање сте.",
    answerDeclined: "Вашиот одговор: не. Нема да присуствувате.",
    answerMaybe: "Вашиот одговор: можеби.",
    thanksTitle: "Проверете ја е-поштата за да го потврдите одговорот",
    thanksBody: "Испративме е-пошта на {email}. Вашиот одговор важи дури откако ќе кликнете на линкот во неа. Линкот важи 48 часа. Не ја добивте? Проверете ја папката за несакана пошта.",
    introDefault: "Почитувани {first name},\n\nСо задоволство ве покануваме на нашиот настан. Деталите се подолу; ве молиме кажете ни дали можете да дојдете.",
    guestName: "",
    aboutEvent: "За настанот",
    programme: "Програма",
    practical: "Практични информации",
    addToCalendar: "Додај во календар",
    dayN: "Ден {n}",
    speaker: "Говорник",
    moderator: "Модератор",
    parallel: "Паралелна сесија",
    reply: "Одговорете на поканата",
    next: "Продолжи",
    yourDetails: "Вашите податоци",
    yourAnswer: "Вашиот одговор",
    change: "Промени",
    personTitle: "Титула (на пример д-р, г-ѓа, г-дин)",
    firstName: "Име",
    lastName: "Презиме",
    country: "Држава",
    phone: "Телефон",
    accessNeeds: "Потреби за пристапност",
    required: "задолжително",
    stillCorrect: "Дали ова е сè уште точно? Го пополнивме она што ни го кажавте претходно. Променете сè што повеќе не е точно.",
    needsOnFile: "Веќе ги имаме вашите потреби за исхрана или пристапност од претходен настан. Внесете ги овде само ако се промениле.",
  },
};

/** The labels of a language ("en" for anything unknown). */
export const guestLabels = (lang: string | null | undefined): GuestLabels => GUEST_LABELS[isRsvpLanguage(lang) ? lang : "en"];
/** Fills "{title}", "{org}", "{email}". */
export const fillLabel = (text: string, v: Record<string, string>): string => text.replace(/\{(\w+)\}/g, (whole, k: string) => v[k] ?? whole);

/** Round 5: the placeholders of the RSVP form's invitation text. */
export const RSVP_INTRO_PLACEHOLDERS = ["{first name}", "{salutation}", "{event title}"] as const;

/**
 * Fills the RSVP form's invitation text. Without a name (the shared link, an
 * unknown guest) {first name} and {salutation} become the language's word
 * for a guest, or are left out with the space before them ("Bonjour,").
 * Unknown placeholders stay as they are. Plain text: the page shows it as
 * text, never as HTML.
 */
export function fillRsvpIntro(text: string, v: { firstName: string; salutation: string; eventTitle: string }, lang: string | null | undefined): string {
  const guest = guestLabels(lang).guestName;
  const map: Record<string, string> = { "first name": v.firstName.trim() || guest, salutation: v.salutation.trim() || guest, "event title": v.eventTitle };
  return text.replace(/([ \t]?)\{\s*([a-z ]+?)\s*\}/gi, (whole, space: string, name: string) => {
    const value = map[name.toLowerCase().replace(/\s+/g, " ")];
    if (value === undefined) return whole;
    return value ? `${space}${value}` : "";
  });
}
